import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const ActivityTracker = () => {
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('today'); // today, week, month
  const [userFilter, setUserFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, [filter, userFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load users, activities, and summary in parallel
      const [usersData, activitiesData, summaryData] = await Promise.all([
        api.getUsers(),
        api.getActivities(filter, userFilter !== 'all' ? userFilter : null),
        api.getActivitySummary(filter)
      ]);
      
      setUsers(usersData);
      // Transform activities to match expected format
      const formattedActivities = activitiesData.map(a => ({
        id: a.id,
        userId: a.user_id,
        userName: a.user_name,
        userRole: a.user_role,
        activity: a.action,
        details: a.description || 'No details',
        timestamp: new Date(a.created_at),
        module: a.module || 'General',
        entityType: a.entity_type,
        entityId: a.entity_id,
        ipAddress: a.ip_address || 'N/A'
      }));
      setActivities(formattedActivities);
      setSummary(summaryData);
    } catch (error) {
      console.error('Failed to load activity data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (activity) => {
    if (activity.includes('Inspection') || activity.includes('Recon')) return '';
    if (activity.includes('Title')) return '';
    if (activity.includes('Transport')) return '🚛';
    if (activity.includes('Time Clock In')) return '';
    if (activity.includes('Time Clock Out')) return '🏁';
    if (activity.includes('Lunch')) return '🍽️';
    if (activity.includes('Expense')) return '';
    if (activity.includes('Vehicle')) return '';
    return '';
  };

  const filteredActivities = userFilter === 'all' 
    ? activities 
    : activities.filter(a => a.userId === parseInt(userFilter));

  // Use summary data from API for user stats
  const getUserStats = () => {
    return summary.map(s => ({
      userId: s.user_id,
      name: s.user_name,
      role: s.user_role,
      totalActivities: parseInt(s.activity_count) || 0,
      modules: s.modules || []
    }));
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Loading activity data...</div>;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Team Activity Tracker</h2>
        <div className="flex gap-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
          </select>
          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Users</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>{user.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* User Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {getUserStats().map(stat => (
          <div key={stat.userId || stat.name} className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="font-semibold text-gray-900">{stat.name}</h3>
                <p className="text-sm text-gray-600">{stat.role}</p>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-blue-600">{stat.totalActivities}</div>
                <div className="text-xs text-gray-500">activities</div>
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              Modules: {Array.isArray(stat.modules) ? stat.modules.filter(Boolean).join(', ') : 'None'}
            </div>
          </div>
        ))}
      </div>
      
      {getUserStats().length === 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center text-yellow-800">
          No activity recorded yet. Activities will appear here as users perform actions in the system.
        </div>
      )}

      {/* Activity Feed */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {filteredActivities.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No activities found</div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredActivities.map(activity => (
                <div key={activity.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">{getActivityIcon(activity.activity)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-medium text-gray-900">{activity.userName}</span>
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                          {activity.userRole}
                        </span>
                        <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded">
                          {activity.module}
                        </span>
                      </div>
                      <div className="text-sm font-medium text-gray-900 mb-1">{activity.activity}</div>
                      <div className="text-sm text-gray-600">{activity.details}</div>
                    </div>
                    <div className="text-xs text-gray-500 text-right whitespace-nowrap">
                      <div className="font-medium text-gray-700">
                        {activity.timestamp.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </div>
                      <div className="text-gray-900 font-mono">
                        {activity.timestamp.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      </div>
                      <div className="text-gray-400 text-[10px] mt-1" title="IP Address">
                        IP: {activity.ipAddress}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityTracker;
